import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Download, Eye, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { MainlineState } from '@services/drbfm.mainline';
import { useSessionExport } from '../history/useDrbfmSessionActions';
import DrbfmPreviewDialog from '../preview/DrbfmPreviewDialog';
import { useDrbfmHtmlPreview } from '../preview/useDrbfmHtmlPreview';

// Rejected and historical rows never pick the session's model (#1592 issuecomment-6248454089).
const NOT_COUNTED = new Set(['rejected', 'superseded']);

const cadModelOf = (anchor: unknown): string | null => {
  if (!anchor || typeof anchor !== 'object' || !('cad_model_id' in anchor)) return null;
  const model = anchor.cad_model_id;
  return typeof model === 'string' && model !== '' ? model : null;
};

/** Null unless every counted anchor names the same model, so none is guessed. */
export function anchoredCadModel(state: MainlineState): string | null {
  const models = new Set(state.failure_modes
    .filter((row) => !NOT_COUNTED.has(row.review_status))
    .map((row) => cadModelOf(row.cad_anchor))
    .filter((model): model is string => model !== null));
  return models.size === 1 ? [...models][0] : null;
}

/** Results summary actions; exportOnly keeps the compact results page focused on export. */
export default function WorkspaceOutboundActions({ sessionId, itemName, cadModelId, exportOnly = false }: {
  exportOnly?: boolean; sessionId: string; itemName: string; cadModelId: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { exportingSessionId, error, exportSession } = useSessionExport();
  const preview = useDrbfmHtmlPreview<{ id: string }>();
  const exporting = exportingSessionId === sessionId;
  return <div className="grid gap-2 sm:ml-auto">
    <div role="group" aria-label={t('drbfm.history.columnActions')} className="flex flex-wrap gap-2">
      {!exportOnly && <><Button type="button" variant="outline" size="sm" onClick={() => navigate('/drbfm')}>
        <Plus aria-hidden="true" />{t('drbfm.results.newAnalysis')}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => { void preview.open({ id: sessionId }); }}>
        <Eye aria-hidden="true" />{t('drbfm.history.preview')}
      </Button></>}
      <Button type="button" variant="outline" size="sm" disabled={exporting}
        onClick={() => { void exportSession({ id: sessionId, itemName }); }}>
        <Download aria-hidden="true" />{exporting ? t('drbfm.results.exporting') : t('drbfm.results.export')}
      </Button>
      {!exportOnly && cadModelId && <Button type="button" variant="outline" size="sm" onClick={() => navigate(
        `/cad-viewer?${new URLSearchParams({ drbfm_session: sessionId, cad_model: cadModelId })}`)}>
        <Box aria-hidden="true" />{t('drbfm.history.view3D')}
      </Button>}
    </div>
    {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
    <DrbfmPreviewDialog open={preview.isOpen} onClose={preview.close} subject={itemName}
      isLoading={preview.isLoading} error={preview.error} html={preview.html} />
  </div>;
}
